/**
 * Componente: Reportes — estadísticas reales de emisión/impresión de
 * licencias. Mismo patrón de estructura que Dashboard/Auditoria: fetch
 * al montar, estado de carga/error, botón Volver.
 *
 * Dos tabs (mismo patrón que Auditoria):
 *  - "Panel": las 3 tarjetas + los 3 gráficos de siempre (sin cambios).
 *  - "Generar informe": rango de fechas + áreas → vista previa en pantalla
 *    y descarga PDF/Excel (endpoints /api/reportes/personalizado[/descargar]).
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceArea,
} from 'recharts';
import { ArrowLeft } from 'lucide-react';
import {
  InsigniaIcono,
  IconAdvertencia,
  IconLicencia,
  IconExito,
  IconPendiente,
} from './icons';
import { formatearFechaCorta } from '../utils/fecha';
import { cerrarSesionSiCorresponde } from '../utils/configurarAxios';
import '../styles/dashboard.css';
import '../styles/disenador.css';
import '../styles/reportes.css';

const API_URL = process.env.REACT_APP_API_URL;

// Recharts no lee variables CSS en la prop fill (necesita el valor
// resuelto), así que se repiten acá los mismos hex que --color-turquesa/
// --color-purpura/etc. en dashboard.css — no inventar colores nuevos.
const COLOR_TURQUESA = '#1a9b8e';
const COLOR_PURPURA = '#6b2d7f';
const COLOR_VERDE_NEON = '#00d9a3';
const COLOR_ERROR = '#e74c3c';
const COLOR_GRIS_MEDIO = '#95a5a6';

const ESTADOS_LABELS = {
  borrador: 'Borrador',
  lista: 'Lista',
  impresa: 'Impresa',
  rechazada: 'Rechazada',
  cancelada: 'Cancelada',
};

const ESTADOS_COLORES = {
  borrador: COLOR_GRIS_MEDIO,
  lista: COLOR_TURQUESA,
  impresa: COLOR_VERDE_NEON,
  rechazada: COLOR_ERROR,
  cancelada: COLOR_PURPURA,
};

// ===== Generar informe: catálogo de áreas + etiquetas =====
// Las etiquetas están redeclaradas a propósito (backend y frontend son
// bundles separados, no hay archivo compartido) — deben coincidir con las
// que usan generarInformePdf.js / generarInformeExcel.js en el backend.
const AREAS_INFORME = [
  { key: 'impresas', label: 'Licencias impresas' },
  { key: 'vencimientos', label: 'Vencimientos' },
  { key: 'pendientes', label: 'Solicitudes de autorización' },
  { key: 'desperdiciadas', label: 'Tarjetas desperdiciadas' },
];

const ETIQUETA_ESTADO_LICENCIA = {
  vigente: 'Vigente',
  proxima_a_vencer: 'Próxima a vencer',
  vencida_en_gracia: 'Vencida (en gracia)',
  vencida_fuera_de_gracia: 'Vencida (fuera de gracia)',
};
const ETIQUETA_TIPO_SOLICITUD = {
  reintento_impresion: 'Reintentos de impresión',
  reimpresion: 'Reimpresiones',
  reactivacion: 'Reactivaciones',
};
const ETIQUETA_ESTADO_SOLICITUD = {
  pendiente: 'Pendientes',
  autorizado: 'Autorizadas',
  rechazado: 'Rechazadas',
};

const ATAJOS_RANGO = [
  { label: 'Última semana', dias: 7 },
  { label: 'Último mes', dias: 30 },
  { label: 'Último trimestre', dias: 90 },
  { label: 'Último semestre', dias: 180 },
  { label: 'Último año', dias: 365 },
];

const RE_ISO = /^\d{4}-\d{2}-\d{2}$/;
// Fecha local (no UTC) a 'YYYY-MM-DD' — mismo criterio que el backend, para
// no correr el día según el huso horario del navegador.
const aISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Tick del eje X de "Últimos 7 días": la etiqueta de hoy se destaca en
// turquesa/negrita, a juego con el ReferenceArea que resalta esa columna.
const TickDia = ({ x, y, payload, hoyEtiqueta }) => {
  const esHoy = payload.value === hoyEtiqueta;
  return (
    <text
      x={x}
      y={y + 12}
      textAnchor="middle"
      fontSize={12}
      fontWeight={esHoy ? 700 : 400}
      fill={esHoy ? COLOR_TURQUESA : '#666'}
    >
      {payload.value}
    </text>
  );
};

const Reportes = ({ onVolver }) => {
  const [tab, setTab] = useState('panel');

  // ----- Panel (sin cambios respecto de antes) -----
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const cargarResumen = async () => {
      try {
        setCargando(true);
        setError('');
        const token = sessionStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/reportes/resumen`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDatos(data);
      } catch (err) {
        console.error('Error al cargar reportes:', err);
        setError(err.response?.data?.mensaje || 'Error al cargar los reportes');
      } finally {
        setCargando(false);
      }
    };
    cargarResumen();
  }, []);

  const porDia = (datos?.por_dia || []).map((d) => ({ ...d, etiqueta: formatearFechaCorta(d.fecha) }));
  // El backend arma por_dia como los últimos 7 días *incluyendo hoy* (ver
  // /api/reportes/resumen), así que el último elemento siempre es hoy —
  // evita recalcular "hoy" en el cliente con su propio huso horario.
  const hoyEtiqueta = porDia.length > 0 ? porDia[porDia.length - 1].etiqueta : null;
  const porEstado = (datos?.por_estado || []).map((e) => ({
    ...e,
    etiqueta: ESTADOS_LABELS[e.estado] || e.estado,
  }));
  const porOperador = datos?.por_operador || [];

  // Notas de "poco volumen de datos" — genéricas, calculadas a partir de
  // la respuesta real del endpoint, no hardcodeadas a los valores de hoy.
  const diasSinActividad = porDia.filter((d) => d.impresas === 0 && d.pendientes === 0).length;
  const mostrarNotaPocaActividad = diasSinActividad >= 6;

  const estadosConDatos = porEstado.filter((e) => e.cantidad > 0).length;
  const mostrarNotaEstadoConcentrado = estadosConDatos === 1;

  // ----- Generar informe -----
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [areasSel, setAreasSel] = useState({
    impresas: false, vencimientos: false, pendientes: false, desperdiciadas: false,
  });
  const [preview, setPreview] = useState(null);
  const [cargandoPreview, setCargandoPreview] = useState(false);
  const [errorInforme, setErrorInforme] = useState('');
  const [descargando, setDescargando] = useState(null); // 'pdf' | 'xlsx' | null

  const areasMarcadas = AREAS_INFORME.filter((a) => areasSel[a.key]).map((a) => a.key);
  const rangoValido = RE_ISO.test(desde) && RE_ISO.test(hasta) && desde <= hasta;
  const puedeGenerar = rangoValido && areasMarcadas.length > 0;
  const ocupado = cargandoPreview || descargando !== null;

  // Cualquier cambio de parámetros invalida la vista previa mostrada.
  const limpiarSalida = () => {
    setPreview(null);
    setErrorInforme('');
  };
  const cambiarFecha = (setter) => (e) => {
    setter(e.target.value);
    limpiarSalida();
  };
  const toggleArea = (key) => {
    setAreasSel((prev) => ({ ...prev, [key]: !prev[key] }));
    limpiarSalida();
  };
  const aplicarAtajo = (dias) => {
    const hoy = new Date();
    const inicio = new Date();
    inicio.setDate(inicio.getDate() - dias);
    setDesde(aISO(inicio));
    setHasta(aISO(hoy));
    limpiarSalida();
  };

  const verResultado = async () => {
    try {
      setCargandoPreview(true);
      setErrorInforme('');
      setPreview(null);
      const token = sessionStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/reportes/personalizado`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { desde, hasta, areas: areasMarcadas.join(',') },
      });
      setPreview(data);
    } catch (err) {
      console.error('Error al generar la vista previa del informe:', err);
      setErrorInforme(err.response?.data?.mensaje || 'Error al generar la vista previa del informe');
    } finally {
      setCargandoPreview(false);
    }
  };

  const descargar = async (formato) => {
    try {
      setDescargando(formato);
      setErrorInforme('');
      const token = sessionStorage.getItem('token');
      const qs = new URLSearchParams({ desde, hasta, areas: areasMarcadas.join(','), formato });
      // fetch (no window.location / <a href>): así se puede mandar el header
      // Authorization como el resto de la app — sin token el endpoint da 401.
      const resp = await fetch(`${API_URL}/reportes/personalizado/descargar?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        let mensaje = 'No se pudo generar el archivo del informe';
        let cuerpo = null;
        try {
          cuerpo = await resp.json();
          mensaje = cuerpo.mensaje || mensaje;
        } catch {
          /* el cuerpo no era JSON */
        }
        // fetch no pasa por el interceptor de axios: si la sesión ya no sirve (cuenta desactivada, municipio
        // suspendido, token vencido) se la cierra acá con la misma regla.
        cerrarSesionSiCorresponde({ status: resp.status, data: cuerpo, url: resp.url || '' });
        throw new Error(mensaje);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      // Nombre de archivo: del Content-Disposition si vino; si no, armado con
      // el mismo criterio que el backend.
      const cd = resp.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const nombre = m ? m[1] : `informe_${desde}_${hasta}.${formato}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = nombre;
      document.body.appendChild(a);
      a.click();
      // Revocar en el próximo tick, no en el mismo: revocar sincrónicamente
      // justo después de .click() puede cancelar la descarga en algunos
      // navegadores (Firefox/Safari).
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
      }, 0);
    } catch (err) {
      console.error('Error al descargar el informe:', err);
      setErrorInforme(err.message || 'Error al descargar el informe');
    } finally {
      setDescargando(null);
    }
  };

  const renderTarjetaArea = (areaKey) => {
    const d = preview[areaKey];
    if (!d) return null;
    const label = AREAS_INFORME.find((a) => a.key === areaKey)?.label || areaKey;
    return (
      <div className="chart-card" key={areaKey}>
        <h3>{label}</h3>

        {areaKey === 'impresas' && (
          <p><strong>Total impresas en el período:</strong> {d.total}</p>
        )}

        {areaKey === 'vencimientos' && (
          <>
            <p><strong>Contribuyentes que vencen en el período:</strong> {d.total_en_rango}</p>
            {['vigente', 'proxima_a_vencer', 'vencida_en_gracia', 'vencida_fuera_de_gracia'].map((k) => (
              <p key={k}>{ETIQUETA_ESTADO_LICENCIA[k]}: {d.por_estado_licencia[k]}</p>
            ))}
            {d.aviso > 0 && (
              <p className="reportes-preview-aviso" style={{ color: COLOR_ERROR }}>
                <IconAdvertencia size={14} style={{ verticalAlign: 'middle' }} /> {d.aviso} vencido(s) fuera de gracia con vencimiento anterior al período — casos urgentes fuera de la ventana.
              </p>
            )}
          </>
        )}

        {areaKey === 'pendientes' && (
          <>
            <p><strong>Solicitudes hechas en el período:</strong> {d.total_en_rango}</p>
            <p className="reportes-preview-sub">Por tipo</p>
            {['reintento_impresion', 'reimpresion', 'reactivacion'].map((k) => (
              <p key={k}>{ETIQUETA_TIPO_SOLICITUD[k]}: {d.por_tipo[k]}</p>
            ))}
            <p className="reportes-preview-sub">Por estado</p>
            {['pendiente', 'autorizado', 'rechazado'].map((k) => (
              <p key={k}>{ETIQUETA_ESTADO_SOLICITUD[k]}: {d.por_estado[k]}</p>
            ))}
            {d.aviso > 0 && (
              <p className="reportes-preview-aviso" style={{ color: COLOR_ERROR }}>
                <IconAdvertencia size={14} style={{ verticalAlign: 'middle' }} /> {d.aviso} solicitud(es) pendiente(s) sin resolver, pedidas antes del período.
              </p>
            )}
          </>
        )}

        {areaKey === 'desperdiciadas' && (
          <p><strong>Tarjetas físicas arruinadas en el período:</strong> {d.total}</p>
        )}
      </div>
    );
  };

  return (
    <section className="dashboard-info">
      <div className="dashboard-section-header">
        {onVolver && (
          <button type="button" className="btn-volver" onClick={onVolver}>
            <ArrowLeft size={16} /> Volver
          </button>
        )}
        <h2>Reportes</h2>
      </div>

      <div className="reportes-tabs">
        <button
          type="button"
          className={`reportes-tab ${tab === 'panel' ? 'activo' : ''}`}
          onClick={() => setTab('panel')}
        >
          Panel
        </button>
        <button
          type="button"
          className={`reportes-tab ${tab === 'informe' ? 'activo' : ''}`}
          onClick={() => setTab('informe')}
        >
          Generar informe
        </button>
      </div>

      {tab === 'panel' ? (
        <>
          {error && (
            <div className="alert alert-error">
              <IconAdvertencia size={18} />
              <p>{error}</p>
            </div>
          )}

          {cargando ? (
            <div className="reportes-loading">
              <div className="loading-spinner"></div>
              <p>Cargando...</p>
            </div>
          ) : datos ? (
            <>
              <section className="dashboard-stats">
                <div className="stat-card">
                  <InsigniaIcono size={64}>
                    <IconLicencia size={30} />
                  </InsigniaIcono>
                  <div className="stat-content">
                    <h3>Emitidas Hoy</h3>
                    <div className="stat-number">{datos.emitidas_hoy}</div>
                    <p className="stat-label">Generadas hoy</p>
                  </div>
                </div>

                <div className="stat-card">
                  <InsigniaIcono size={64}>
                    <IconExito size={30} />
                  </InsigniaIcono>
                  <div className="stat-content">
                    <h3>Impresas Hoy</h3>
                    <div className="stat-number">{datos.impresas_hoy}</div>
                    <p className="stat-label">Impresas hoy</p>
                  </div>
                </div>

                <div className="stat-card">
                  <InsigniaIcono size={64}>
                    <IconPendiente size={30} />
                  </InsigniaIcono>
                  <div className="stat-content">
                    <h3>Pendientes</h3>
                    <div className="stat-number">{datos.pendientes_ahora}</div>
                    <p className="stat-label">En cola</p>
                  </div>
                </div>
              </section>

              <div className="reportes-charts-grid">
                <div className="chart-card chart-card--full">
                  <h3>Últimos 7 días</h3>
                  {mostrarNotaPocaActividad && (
                    <p className="reportes-nota reportes-nota--arriba">
                      Todavía hay poca actividad registrada — este gráfico se va a completar con el uso diario real.
                    </p>
                  )}
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={porDia}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      {hoyEtiqueta && (
                        <ReferenceArea
                          x1={hoyEtiqueta}
                          x2={hoyEtiqueta}
                          fill={COLOR_TURQUESA}
                          fillOpacity={0.08}
                          stroke={COLOR_TURQUESA}
                          strokeOpacity={0.25}
                          ifOverflow="visible"
                        />
                      )}
                      <XAxis
                        dataKey="etiqueta"
                        tick={(props) => <TickDia {...props} hoyEtiqueta={hoyEtiqueta} />}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="impresas" name="Impresas" fill={COLOR_TURQUESA} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="pendientes" name="Pendientes" fill={COLOR_PURPURA} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-card">
                  <h3>Licencias por estado</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={porEstado} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="etiqueta" width={80} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="cantidad" name="Cantidad" radius={[0, 4, 4, 0]}>
                        {porEstado.map((e) => (
                          <Cell key={e.estado} fill={ESTADOS_COLORES[e.estado] || COLOR_GRIS_MEDIO} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  {mostrarNotaEstadoConcentrado && (
                    <p className="reportes-nota">
                      Todo concentrado en un solo estado — normal con poco volumen de licencias todavía. Con más
                      actividad, vas a ver movimiento entre los distintos estados.
                    </p>
                  )}
                </div>

                <div className="chart-card">
                  <h3>Actividad por operador</h3>
                  {porOperador.length === 0 ? (
                    <p className="stat-label">Todavía no hay impresiones registradas.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={porOperador} layout="vertical" margin={{ left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                        <YAxis type="category" dataKey="nombre_completo" width={110} tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="impresiones" name="Impresiones" fill={COLOR_TURQUESA} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                  <p className="reportes-nota">
                    Cuenta cada impresión confirmada por persona, no licencias distintas — un reintento o una
                    reimpresión suman otra vez para quien la confirmó.
                  </p>
                </div>
              </div>
            </>
          ) : null}
        </>
      ) : (
        <div className="reportes-informe">
          <div className="reportes-informe-form">
            <div>
              <p className="reportes-campo-titulo">Rango de fechas</p>
              <div className="reportes-informe-atajos">
                {ATAJOS_RANGO.map((a) => (
                  <button
                    type="button"
                    key={a.dias}
                    className="reportes-atajo"
                    onClick={() => aplicarAtajo(a.dias)}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
              <div className="reportes-informe-fechas">
                <div className="reportes-campo">
                  <label htmlFor="informe-desde">Desde</label>
                  <input
                    id="informe-desde"
                    type="date"
                    value={desde}
                    max={hasta || undefined}
                    onChange={cambiarFecha(setDesde)}
                  />
                </div>
                <div className="reportes-campo">
                  <label htmlFor="informe-hasta">Hasta</label>
                  <input
                    id="informe-hasta"
                    type="date"
                    value={hasta}
                    min={desde || undefined}
                    onChange={cambiarFecha(setHasta)}
                  />
                </div>
              </div>
            </div>

            <div>
              <p className="reportes-campo-titulo">Áreas a incluir</p>
              <div className="reportes-informe-areas">
                {AREAS_INFORME.map((a) => (
                  <label key={a.key} className="reportes-area-check">
                    <input
                      type="checkbox"
                      checked={!!areasSel[a.key]}
                      onChange={() => toggleArea(a.key)}
                    />
                    {a.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="reportes-informe-acciones">
              <button
                type="button"
                className="reportes-btn reportes-btn--primario"
                onClick={verResultado}
                disabled={!puedeGenerar || ocupado}
              >
                {cargandoPreview ? 'Generando...' : 'Ver resultado'}
              </button>
              <button
                type="button"
                className="reportes-btn"
                onClick={() => descargar('pdf')}
                disabled={!puedeGenerar || ocupado}
              >
                {descargando === 'pdf' ? 'Descargando...' : 'Descargar PDF'}
              </button>
              <button
                type="button"
                className="reportes-btn"
                onClick={() => descargar('xlsx')}
                disabled={!puedeGenerar || ocupado}
              >
                {descargando === 'xlsx' ? 'Descargando...' : 'Descargar Excel'}
              </button>
            </div>

            {errorInforme && (
              <div className="alert alert-error">
                <IconAdvertencia size={18} />
                <p>{errorInforme}</p>
              </div>
            )}
          </div>

          {cargandoPreview ? (
            <div className="reportes-loading">
              <div className="loading-spinner"></div>
              <p>Generando vista previa...</p>
            </div>
          ) : preview ? (
            <div className="reportes-preview-grid">
              {(preview.areas || []).map(renderTarjetaArea)}
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
};

export default Reportes;
